import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { UserService } from '@oa/shared-core';
import CouponList from '@app/models-internal/coupon-list';
import CustomerViewModel from '@app/models-internal/customer-view-model';
import { SubscriptionChangeRequest } from '@oa/shared-core';
import ProRata from '@app/models-internal/pro-rata';
import { ICreditCard } from '@app/shared2-components/stripe-form/stripe-form.component';
import { Subject } from 'rxjs';

export interface PaymentResultModel {
  listingId: number;

  /**
   * The name of the workflow that is the source of the payment result
   */
  workflowName: string;

  isSuccess: boolean;
}

@Injectable()
export class PaymentService {
  private readonly _componentName = 'PaymentService';

  public paymentSuccess = new Subject<PaymentResultModel>();

  private _activeCoupons: CouponList = {
    items: [],
  } as CouponList;

  constructor(private http: HttpClient, private userService: UserService) {}

  /**
   * Gets the source ID from a stripe source object (different property name from stripe test  and prod)
   */
  public getSourceId(source: any) {
    const sourceId = source.sourceId;
    const stripeSourceId = source.stripeSourceId;

    return sourceId || stripeSourceId;
  }

  public isTest() {
    // TODO: Implement an environment check
    return false;
  }

  /**
   * Gets active coupons.  Active coupons are coupons entered at some point in a workflow, but not deferred until
   * another part of a purchasing workflow (e.g. coupons entered in the booking widget, saved in this service,
   * and then retrieved during the payment step.)
   */
  public get activeCoupons(): CouponList {
    return this._activeCoupons;
  }

  /**
   * Sets active coupons.  Active coupons are coupons entered at some point in a workflow, but not deferred until
   * another part of a purchasing workflow (e.g. coupons entered in the booking widget, saved in this service,
   * and then retrieved during the payment step.)
   */
  public set activeCoupons(v: CouponList) {
    this._activeCoupons = v;
  }

  /**
   * Gets the active coupon (assuming there is only one).
   */
  public get activeCoupon(): string {
    if (this.activeCoupons && this.activeCoupons.items.length > 0) {
      return this.activeCoupons.items[0].couponCode;
    } else if (this.activeCoupons && this.activeCoupons.items.length > 1) {
      throw Error('Multiple promo codes not supported.');
    }

    return undefined;
  }

  public getActiveBookingCoupons(): string[] {
    if (!this.activeCoupons || this.activeCoupons.items.length === 0) {
      return [];
    }

    return this.activeCoupons.items.filter((x) => x.redemptionType == 2).map((x) => x.couponCode);
  }

  public getActiveMembershipCoupons(): string[] {
    if (!this.activeCoupons || this.activeCoupons.items.length === 0) {
      return [];
    }

    return this.activeCoupons.items.filter((x) => x.redemptionType == 1).map((x) => x.couponCode);
  }

  /**
   * Resets the active coupons
   */
  public resetActiveCoupons() {
    this._activeCoupons = {
      items: [],
    } as CouponList;
  }

  /**
   * Gets payment info for a stripe payment id
   * @param stripePaymentId
   */
  public getPaymentInfo(paymentId: string) {
    return this.http.get<any>(`stripe/${paymentId}`);
  }

  /**
   * Gets payment info for a stripe charge id
   * @param chargeId
   */
  public getChargeInfo(chargeId: string) {
    return this.http.get<any>(`stripe/charge/${chargeId}`);
  }

  /**
   * Gets payment customer info for a user account
   * @param userAccountId
   */
  public getCustomerInfo(userAccountId: number) {
    const url = `useraccount/customer/${userAccountId}`;
    return this.http.get<CustomerViewModel>(url);
  }

  /**
   * Gets the customer data from the payment system
   * @param customerId
   * @returns
   */
  public getCustomer(customerId: string) {
    const url = `customer/${customerId}`;
    return this.http.get<object>(url);
  }

  /**
   * Gets the pro rate amount for a plan change
   * @param request
   */
  public retrieveProRate(request: SubscriptionChangeRequest) {
    const url = 'subscription/change/preview';

    return this.http.post<ProRata>(url, request);
  }

  public isValidCard(card: ICreditCard) {
    if (!card) {
      return false;
    }

    if (!card.cvc || !card.exp || !card.name || !card.number) {
      return false;
    } else {
      return true;
    }
  }
}
